'use client';

import { Indicator } from '$components/Indicator';
import { TimelineContent } from '$components/Timeline/TimelineContent';
import { TimelineImage } from '$components/Timeline/TimelineImage';
import cn from 'classnames';
import { HTMLAttributes, useEffect, useRef, useState } from 'react';

import { ITimelineItem } from './';

export function TimelineItem(
  props: ITimelineItem & HTMLAttributes<HTMLElement>,
) {
  const { className, index, item, length } = props;

  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  let options = {
    rootMargin: '0px 0px -50% 0px',
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setVisible(
          entry.boundingClientRect.y < 0 ? true : entry.isIntersecting,
        );
      });
    }, options);
    observer.observe(domRef.current);
    return () => {
      observer.disconnect();
    };
  });

  return (
    <div
      ref={domRef}
      className={cn(
        className,
        'flex flex-col-reverse md:flex-row',
        index % 2 === 1 ? 'md:flex-row-reverse' : '',
      )}
    >
      {index === 0 && (
        <>
          <div
            className={cn(
              'md:block hidden absolute left-0 top-[50%] md:left-[50%] border-l-2 -ml-[1px] border-50 duration-500 z-10',
              !isVisible ? 'h-0' : 'h-1/2',
            )}
          ></div>
          <div
            className={cn(
              'block md:hidden absolute top-0 left-0 md:left-[50%] border-l-2 -ml-[1px] border-50 duration-500 z-10',
              !isVisible ? 'h-0' : 'h-full',
            )}
          ></div>
          <div
            className={cn(
              'block md:hidden h-1/2 absolute left-0 top-0 md:left-[50%] border-l-2 -ml-[1px] border-grey-20 border-dashed duration-500',
            )}
          ></div>
        </>
      )}
      {index !== 0 && (
        <div
          className={cn(
            'h-1/2 absolute left-0 top-0 md:left-[50%] border-l-2 -ml-[1px] border-grey-20 border-dashed duration-500',
            index + 1 === length ? 'hidden md:block' : '',
          )}
        ></div>
      )}
      {index !== 0 && index + 1 < length && (
        <div
          className={cn(
            'absolute top-0 left-0 md:left-[50%] border-l-2 -ml-[1px] border-50 duration-500 z-10',
            !isVisible ? 'h-0' : 'h-full',
          )}
        ></div>
      )}
      {index + 1 < length && (
        <div
          className={cn(
            'h-1/2 absolute bottom-0 left-0 md:left-[50%] border-l-2 -ml-[1px] border-grey-20 border-dashed duration-500',
          )}
        ></div>
      )}
      {index + 1 === length && (
        <>
          <div
            className={cn(
              'md:block hidden absolute top-0 left-0 md:left-[50%] border-l-2 -ml-[1px] border-50 duration-500 z-10',
              !isVisible ? 'h-0' : 'h-1/2',
            )}
          ></div>
        </>
      )}

      <div
        className={cn(
          'absolute left-0 md:left-[50%] top-0 md:top-[50%] -ml-6 -mt-6 z-50',
        )}
      >
        <Indicator
          className={!isVisible ? 'bg-white' : '!border-50 bg-white'}
          classNameInner={!isVisible ? '' : '!bg-50'}
          duration={600}
          action={null}
          index={null}
          status={'active'}
        />
      </div>

      <div
        className={cn(
          'w-1/4 absolute md:block hidden top-[50%] border-t-2 -mt-[1px] z-[-1] duration-500',
          index % 2 === 0 ? 'left-[25%]' : 'left-[50%]',
          !isVisible ? 'border-grey-20 border-dashed' : 'border-50',
        )}
      ></div>

      <div
        className={cn(
          'w-full pl-10 pt-8 md:p-0 md:w-1/2 flex items-center justify-center',
        )}
      >
        <div
          className={cn(
            'mb-12 md:mb-0 aspect-[1/1] w-full md:w-1/2 bg-white flex items-center justify-center rounded-full border-2 duration-500',
            index % 2 === 0 ? 'md:mr-32' : 'md:ml-32',
            !isVisible ? 'border-grey-20' : 'border-50',
          )}
        >
          <TimelineImage
            key={index}
            active={true}
            index={index}
            assets={item.assetsCollection.items}
            coverClass={'rounded-full w-[75%] h-[75%] object-cover'}
          />
        </div>
      </div>
      <div className={cn('w-full md:w-1/2')}>
        <TimelineContent
          item={item.text}
          index={index}
          right={index % 2 === 1}
        />
      </div>
    </div>
  );
}
